import React from 'react';
import { AlertTriangle, CheckCircle, Clock, GitCompare } from 'lucide-react';

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.ceil(bytes / 1024)} KB`;
};

const LinkedSourcePreview = ({ state }) => {
  if (!state) return null;
  if (!state.success) {
    return (
      <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="linked-fetch-error">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {state.message}
      </div>
    );
  }

  const changedLines = (state.comparison?.lines || []).filter(line => line.kind !== 'unchanged').slice(0, 30);
  const metadata = [
    ['Title', state.document_title || state.source_filename || 'Detected document'],
    ['Type', state.content_type],
    ['Downloaded', formatBytes(state.content_length)],
    ['Pages', state.page_count || '—'],
    ['Words', state.word_count?.toLocaleString?.() || state.word_count || '—'],
    ['Search chunks', state.chunk_count ?? '—'],
  ];

  return (
    <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="linked-fetch-preview">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900"><CheckCircle size={14} /> Preview before saving</p>
          <p className="mt-0.5 text-xs text-emerald-800">This is the content Qflow will index after you confirm.</p>
        </div>
        {state.retrieved_at && (
          <span className="flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[10px] text-gray-600"><Clock size={11} /> Retrieved {new Date(state.retrieved_at).toLocaleString()}</span>
        )}
      </div>

      <div className="grid gap-2 rounded-md border border-emerald-100 bg-white p-2 sm:grid-cols-2 lg:grid-cols-3">
        {metadata.map(([label, value]) => (
          <div key={label} className="min-w-0 text-[11px]"><span className="text-gray-400">{label}</span><p className="truncate font-medium text-gray-700" title={String(value || '')}>{value || '—'}</p></div>
        ))}
      </div>

      <div className="rounded-md border border-blue-100 bg-blue-50/60 p-2" data-testid="linked-change-preview">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-900"><GitCompare size={13} /> {state.comparison?.summary || 'Initial document preview'}</p>
        {state.source_last_modified_at && <p className="mt-1 text-[10px] text-blue-700">Source reports last modified: {state.source_last_modified_at}</p>}
        {changedLines.length > 0 && (
          <div className="mt-2 max-h-32 overflow-auto rounded border bg-slate-950 font-mono text-[10px]">
            {changedLines.map((line, index) => (
              <div key={`${line.kind}-${index}`} className={`px-2 py-0.5 ${line.kind === 'added' ? 'bg-emerald-950 text-emerald-200' : 'bg-rose-950 text-rose-200'}`}>
                <span className="mr-2">{line.kind === 'added' ? '+' : '−'}</span>{line.text || ' '}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-emerald-900">Extracted content</p>
        <p className="max-h-52 overflow-y-auto whitespace-pre-wrap rounded-md border border-emerald-100 bg-white p-2 text-xs leading-relaxed text-gray-700">{state.preview}</p>
        {state.preview_truncated && <p className="mt-1 text-[10px] text-gray-500">Preview shortened for display; the complete extracted content will be indexed.</p>}
      </div>
    </div>
  );
};

export default LinkedSourcePreview;
